import { useId } from "react"

import { Modal } from "../shared/Modal"
import type { HospitalityCategoryOption } from "./guestHospitalityOptions"
import type { StayGuestHospitalityTarget } from "./stayData"

type GuestHospitalityFormProps = {
  canAddNote: boolean
  category: HospitalityCategoryOption
  description: string
  error: string | null
  isSaving: boolean
  onAddNote: () => void
  onClose: () => void
  onDescriptionChange: (description: string) => void
  onTargetChange: (targetId: string) => void
  selectedTargetId: string | null
  targets: readonly StayGuestHospitalityTarget[]
}

export function GuestHospitalityForm({
  canAddNote,
  category,
  description,
  error,
  isSaving,
  onAddNote,
  onClose,
  onDescriptionChange,
  onTargetChange,
  selectedTargetId,
  targets,
}: GuestHospitalityFormProps) {
  const descriptionId = useId()
  const formId = useId()
  const modalCopy = hospitalityModalCopy[category.value]

  return (
    <Modal
      footer={(
        <>
          <button
            className="h-8 rounded-[4px] border border-[#344149] bg-white px-3 text-xs font-medium leading-4 text-[#344149] hover:bg-[#f5f5f5] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isSaving}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className="h-8 rounded-[4px] bg-[#344149] px-3 text-xs font-medium leading-4 text-white hover:bg-[#263138] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!canAddNote}
            form={formId}
            type="submit"
          >
            Add
          </button>
        </>
      )}
      onClose={onClose}
      open
      title={modalCopy.title}
    >
      <form
        className="flex flex-col gap-6"
        id={formId}
        onSubmit={(event) => {
          event.preventDefault()
          onAddNote()
        }}
      >
        <section className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h3 className="text-base font-medium leading-[19px] text-black">Who is this for?</h3>
            <p className="text-xs font-normal leading-[15px] text-[#6b7078]">
              Choose a guest, or Whole party if it applies to everyone.
            </p>
          </div>

          <div aria-label="Guest" className="flex flex-wrap gap-2.5" role="group">
            {targets.map((target) => {
              const isSelected = selectedTargetId === target.id

              return (
                <button
                  aria-pressed={isSelected}
                  className={[
                    "inline-flex h-8 items-center gap-2 overflow-hidden rounded-[24px] border px-3 py-2 text-[13px] leading-normal whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                    isSelected
                      ? "border-[#344149] bg-[#d2dadf] font-medium text-[#344149]"
                      : "border-[#d4d4d4] bg-[#f5f5f5] font-normal text-black hover:border-[#344149] hover:bg-[#e7ebee] hover:font-medium hover:text-[#344149]",
                  ].join(" ")}
                  disabled={isSaving}
                  key={target.id || "whole-party"}
                  onClick={() => onTargetChange(target.id)}
                  type="button"
                >
                  {hospitalityTargetChipLabel(target)}
                </button>
              )
            })}
          </div>
        </section>

        <section className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h3 className="text-base font-medium leading-[19px] text-black">What should the team know?</h3>
            <p className="text-xs font-normal leading-[15px] text-[#6b7078]">{modalCopy.description}</p>
          </div>

          <label className="sr-only" htmlFor={descriptionId}>
            Hospitality note
          </label>
          <input
            className="h-10 min-w-0 rounded-[6px] border border-[#a3a3a3] bg-white px-2.5 text-xs font-normal text-black outline-none placeholder:text-[#a3a3a3] focus:border-[#337ab7] focus:ring-2 focus:ring-[#337ab7]/20"
            disabled={isSaving}
            id={descriptionId}
            onChange={(event) => onDescriptionChange(event.target.value)}
            placeholder={modalCopy.placeholder}
            type="text"
            value={description}
          />
        </section>

        {error ? (
          <p className="rounded-[4px] border border-[#e0b4b4] bg-[#fff2f2] px-3 py-2 text-sm font-bold text-[#873230]">
            {error}
          </p>
        ) : null}
      </form>
    </Modal>
  )
}

const hospitalityModalCopy = {
  food_and_drink: {
    description:
      "Capture food and drink preferences the team can actually use, including pacing, portion style, drinks, and repeatable hosting cues.",
    placeholder: "Add a food and drink note...",
    title: "ADD FOOD & DRINK NOTE",
  },
  guiding: {
    description:
      "Note pace, drive length, focus areas, conversation style, and any guest interests that will help guiding feel better tuned.",
    placeholder: "Add a guide note...",
    title: "ADD GUIDE NOTE",
  },
  hosting: {
    description:
      "Capture privacy, surprises, room energy, hosting rhythm, and any room or housekeeping cues the team can pick up and use consistently.",
    placeholder: "Add a hosting note...",
    title: "ADD HOSTING NOTE",
  },
  other_hospitality: {
    description:
      "Capture any learned hospitality detail that does not fit the main categories but would still help the next team member personalise the stay.",
    placeholder: "Add note...",
    title: "ADD OTHER NOTE",
  },
} as const

function hospitalityTargetChipLabel(target: StayGuestHospitalityTarget) {
  if (!target.id) return target.label

  return target.label.split(" ")[0] || target.label
}
